import * as React from 'react'

import { Box, Button, Icon, RadioButton, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import { GetMeetingDocument, GetMeetingQuery } from 'generated/resources/graphql'
import { AddTalkingPointMutationDocument } from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextEditor from 'modules/core/components/TextEditor'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import MeetingSectionTitle from '../components/MeetingSectionTitle'

interface Props {
  talkingPoint?: NonNullable<
    NonNullable<GetMeetingQuery['oneOnOnes']['meeting']>['talkingPointsConnection']['nodes']
  >[number]
  meetingId: number
  participant: NonNullable<
    NonNullable<GetMeetingQuery['oneOnOnes']['meeting']>['oneOnOne']['firstParticipant']
  >
  readOnly: boolean
  displayBorders?: boolean
}

interface UpdateTalkingPoint {
  meetingId: number | undefined
  participantId: number | undefined
  notes: string | null | undefined
  privateNotes: string | null | undefined
  moodScore: number | null | undefined
}

export default function TalkingPoints({
  talkingPoint,
  meetingId,
  participant,
  readOnly,
  displayBorders = true,
}: Props) {
  const [updateTalkingPoint, {}] = useMutation(AddTalkingPointMutationDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.addOneononesTalkingPoint,
  })

  const moods = [
    i18n.t('one_on_ones.meeting.talking_points.moods.0'),
    i18n.t('one_on_ones.meeting.talking_points.moods.1'),
    i18n.t('one_on_ones.meeting.talking_points.moods.2'),
    i18n.t('one_on_ones.meeting.talking_points.moods.3'),
    i18n.t('one_on_ones.meeting.talking_points.moods.4'),
  ]
  const mood = moods[Number(talkingPoint?.moodScore)] || undefined

  const title = readOnly
    ? i18n.t('one_on_ones.meeting.talking_points.read_only.title', {
        participant_first_name: participant.firstName,
      })
    : i18n.t('one_on_ones.meeting.talking_points.editable.title')

  const form = useForm<UpdateTalkingPoint>({
    defaultValues: {
      meetingId: meetingId,
      participantId: participant.id,
      notes: talkingPoint?.notes,
      privateNotes: readOnly ? '' : talkingPoint?.privateNotes,
      moodScore: talkingPoint?.moodScore,
    },
    onSubmit: async (data) => {
      const response = await updateTalkingPoint({
        variables: {
          meetingId: meetingId,
          participantId: participant.id,
          notes: data.notes,
          privateNotes: data.privateNotes,
          moodScore: data.moodScore,
        },
        refetchQueries: [
          {
            query: GetMeetingDocument,
            variables: {
              id: meetingId,
            },
          },
        ],
      })

      const handled = handleResult(response, () => {
        // TODO: do something with form errors
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        track(trackingEvents.one_on_ones.saveTalkingPointOrPrivateNote, {
          talking_point: Boolean(data.notes),
          private_note: Boolean(data.privateNotes),
        })
        const values = form.values
        form.resetForm({ values })
      }

      return handled
    },
  })

  // (June 1 2023) As per Product requirements - disabling and hiding moods from the UI until further decision
  const showMoodsInUI = false
  const notes = form.values.notes
  const isNotesEmpty = !notes || notes === ''

  return (
    <>
      <Box
        gap="s16"
        paddingBottom="s32"
        paddingTop={displayBorders ? 's32' : null}
        component="ul"
        border={
          displayBorders
            ? {
                top: {
                  width: 's1',
                  color: 'grey400',
                  style: 'solid',
                },
              }
            : undefined
        }
      >
        <MeetingSectionTitle
          title={title}
          isBeta={false}
          button={
            readOnly ? undefined : (
              <Button
                onClick={() => {
                  form.handleSubmit()
                }}
                type="primary"
                disabled={!form.dirty || form.isSubmitting}
                iconName="check"
              >
                {i18n.t('common.save')}
              </Button>
            )
          }
        />
        <Box flexDirection="row" alignItems="center" gap="s8" paddingBottom="s8">
          <UserAvatar
            size="s40"
            user={{ avatar: participant.access?.avatar, fullName: participant.fullName }}
          />
          <Text.Normal>{participant.fullName}</Text.Normal>
          {showMoodsInUI && mood && <Text.Normal>{' · ' + mood}</Text.Normal>}
        </Box>

        <Box gap="s16" flexDirection="column" width="full">
          {readOnly ? (
            isNotesEmpty ? (
              <Box
                border={{
                  all: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey400',
                  },
                }}
                justifyContent="center"
                alignItems="center"
                borderRadius={{
                  all: 'abs008',
                }}
                padding="s24"
              >
                <Text.Normal color="grey700" weight="medium">
                  {i18n.t('one_on_ones.meeting.talking_points.empty_state')}
                </Text.Normal>
              </Box>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(notes),
                }}
              />
            )
          ) : (
            <>
              <Box gap="s16" flexDirection="row" width="full">
                <Box
                  gap="s8"
                  alignItems="stretch"
                  width="full"
                  aria-label={i18n.t('one_on_ones.meeting.talking_points.title')}
                >
                  <Text.Normal>
                    {i18n.t('one_on_ones.meeting.talking_points.editable.notes_question')}
                  </Text.Normal>
                  <TextEditor
                    placeholder=""
                    height="s300"
                    initialEditorState={{
                      data: talkingPoint?.notes || '',
                    }}
                    onChange={(note) => form.setValues({ ...form.values, notes: note as string })}
                  />
                </Box>
                <Box gap="s8" alignItems="flexStart" paddingX="s8">
                  <Box flexDirection="row">
                    <Icon.Medium icon={'eye-invisible'} />
                    <Text.Normal>
                      {i18n.t('one_on_ones.meeting.talking_points.editable.private_notes')}
                    </Text.Normal>
                  </Box>

                  <TextArea
                    minRows={14}
                    width={260}
                    value={form.values.privateNotes || ''}
                    onChange={(privateNote: string) =>
                      form.setValues({ ...form.values, privateNotes: privateNote })
                    }
                  />
                </Box>
              </Box>
              {showMoodsInUI && (
                <Box paddingTop="s16">
                  <Text.Normal>
                    {i18n.t('one_on_ones.meeting.talking_points.editable.mood_score_question')}
                  </Text.Normal>
                  <Box gap="s48" flexDirection="row" width="full" paddingY="s8">
                    {moods.map((mood, index) => (
                      <Box key={index}>
                        <RadioButton
                          label={mood}
                          checked={index === form.values.moodScore}
                          onChange={(event) => {
                            form.setValues({
                              ...form.values,
                              moodScore: Number(event.target.value),
                            })
                          }}
                          value={String(index)}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  )
}
